import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import Table from "@components/Table";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { ActionsType } from "src/interfaces/Common";
import { MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import useSWRMutation from "swr/mutation";
import AnimationFadeIn from "@components/AnimationFadeIn";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import DescriptionIcon from "@mui/icons-material/Description";
import ModalCst from "@components/ModalCst";
import { Grid, TextField, Typography, useTheme, Box } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import EuroIcon from "@mui/icons-material/Euro";

interface TableOdaProps {
  stats: any;
  mutateBook: any;
}

const TableOda: React.FC<TableOdaProps> = ({ stats, mutateBook }) => {
  const theme = useTheme();
  const { t } = useTranslation(["accounting-page"]);
  const [idCostRejected, setIdCostRejected] = useState<string>();
  const [openModalReject, setOpenModalReject] = useState(false);
  const [costApproved, setCostApproved] = useState<{}>();
  const [openModalApproved, setOpenModalApproved] = useState(false);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(`${ServicesURL.getProjectsCost}`, PagesID["accounting"], {
      status: "APPROVED, PO_ISSUED",
      requestType: "RDA",
    });

  const { trigger: triggerCostRejected } = useSWRMutation(
    `${ServicesURL.postProjectsCost}/${idCostRejected}/action/reject`,
    (url: string) => {
      return fetcher<any>(url, MethodType.POST);
    },
  );

  const handleReject = React.useCallback(async () => {
    if (idCostRejected) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerCostRejected();
        const newData = [
          ...dataTable.output.filter((el: any) => el.id !== idCostRejected),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        mutateBook && mutateBook();
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdCostRejected(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [dataTable, idCostRejected, mutate, mutateBook, triggerCostRejected]);

  const dataFormat = useMemo(() => {
    if (!dataTable?.output) return [];

    return dataTable.output.map((el: any) => {
      el.accounting_type = "ODA";

      if (el.budget_project_categorization) {
        el.import = el.budget_project_categorization?.allocated_budget || "-";
      }

      if (
        el.budget_project_categorization &&
        el.budget_project_categorization.account
      ) {
        el.account = {
          label: el.budget_project_categorization.account?.sap_number || "-",
        };
      }

      if (el.budget_project) {
        el.project_name = el.budget_project?.title || "-";
      }

      if (el.contract) {
        el.contract = el.contract?.sap_code || "-";
      }

      return el;
    });
  }, [dataTable?.output]);

  const filteredColumns = useMemo(() => {
    if (columns) {
      return columns.map((el) => {
        return { ...el };
      });
    }
  }, [columns]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "button": {
          switch (action.key) {
            case "approve-accounting":
              return {
                ...action,
                getIcon: (data: any) => (
                  <DescriptionIcon
                    sx={{
                      fill:
                        data.status === "APPROVED" ? "#1B1B1B !important" : "",
                      backgroundColor:
                        data.status === "APPROVED" ? "#038BA5 !important" : "",
                      padding: data.status === "APPROVED" ? "4px" : "",
                      borderRadius: data.status === "APPROVED" ? "4px" : "",
                    }}
                  />
                ),
                callBack: (data: any) => {
                  setCostApproved(data);
                  setOpenModalApproved(true);
                },
              };
            default:
              return action;
          }
        }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    //FUNZIONE PER ABILITAZIONE/ DISABILITAZIONE CAMPI IN BASE A VALORI DELLA ROW
    configRowNew = configRowNew.map((el: ActionsType) => {
      if (
        el.key === "approve-accounting" &&
        row.original?.status !== "APPROVED"
      ) {
        return { ...el, disabled: true };
      }

      return { ...el };
    });

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      return action;
    });
  }, [actionsTopToolbar]);

  const renderCostField = (
    label: string,
    count: number | string = 0,
    value: number | string = 0,
  ) => (
    <Grid item xs={12} sm={4}>
      <Typography sx={{ mb: 1.5, fontWeight: "bold" }}>{label}</Typography>
      <TextField
        type="text"
        size="small"
        value={new Intl.NumberFormat("it-IT", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value) || 0)}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0, pr: 0 }}>
              <Box sx={{ mr: 1 }}>
                <b>{count} -</b>
              </Box>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <EuroIcon />
            </InputAdornment>
          ),
        }}
        variant="outlined"
        fullWidth
        inputProps={{
          style: { paddingLeft: 0 },
        }}
      />
    </Grid>
  );

  const statsFields = [
    {
      label: t("oda.total"),
      count: stats?.total_count,
      value: stats?.total_value,
    },
    {
      label: t("oda.to_be_issued"),
      count: stats?.to_be_issued_count,
      value: stats?.to_be_issued_value,
    },
    {
      label: t("oda.issued"),
      count: stats?.issued_count,
      value: stats?.issued_value,
    },
  ];

  return (
    <AnimationFadeIn>
      <Grid container>
        <Grid
          container
          direction="column"
          sx={{
            backgroundColor: theme.palette.background.paper,
            padding: 3,
            width: "100%",
            borderRadius: 1,
            mb: 1,
          }}
        >
          <Grid container spacing={3} sx={{ mb: 1 }}>
            {statsFields.map(({ label, count, value }) =>
              renderCostField(label, count, value),
            )}
          </Grid>
        </Grid>
      </Grid>
      <Table
        id={PagesID.accounting}
        data={dataFormat || []}
        columns={filteredColumns || []}
        configurationTopToolbar={actionsToolbar}
        renderRowActions={customRenderRowActions}
        configurationActionForRow={configurationActionForRow}
        numberOfActions={configurationActionForRow?.length}
        totalPages={dataTable?.metadata?.total_elements}
        modalTitle={t("hero-title")?.replace("-", "") || ""}
      />
      <ModalCst
        open={openModalApproved}
        setOpen={setOpenModalApproved}
        typeOfModal={"ManageAccounting"}
        onSubmit={() => {
          setCostApproved(undefined);
          setOpenModalApproved(false);
          mutate && mutate();
          mutateBook && mutateBook();
        }}
        data={costApproved}
      />
      <ModalConfirmsCst
        open={!!openModalReject}
        title={t("reject-accounting", { ns: "modal" })}
        description={t("confirm-reject-accounting", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalReject(!openModalReject);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleReject()}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default TableOda;
